import axios from 'axios';

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/?$/, '/');
const BENCHMARK_BASE = `${API_ROOT}synctrack/api/benchmark`;

const benchmarkClient = axios.create({
  baseURL: BENCHMARK_BASE,
  timeout: 120000,
});

benchmarkClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem('tenantId');
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  return config;
});

benchmarkClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Benchmark API request failed';
    return Promise.reject(new Error(message));
  }
);

const toFormData = (fields) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (key === 'columnMapping' && typeof value === 'object') {
      form.append(key, JSON.stringify(value));
    } else {
      form.append(key, value);
    }
  });
  return form;
};

export const projectsApi = {
  list: (params = {}) => benchmarkClient.get('/projects', { params }),
  getById: (id) => benchmarkClient.get(`/projects/${id}`),
  create: (body) => benchmarkClient.post('/projects', body),
  update: (id, body) => benchmarkClient.put(`/projects/${id}`, body),
  archive: (id) => benchmarkClient.delete(`/projects/${id}`),
};

export const contractsApi = {
  list: (projectId) =>
    benchmarkClient.get('/contracts', { params: projectId ? { projectId } : {} }),
  getById: (id) => benchmarkClient.get(`/contracts/${id}`),
  create: (body) => benchmarkClient.post('/contracts', body),
  update: (id, body) => benchmarkClient.put(`/contracts/${id}`, body),
  remove: (id) => benchmarkClient.delete(`/contracts/${id}`),
};

export const structuresApi = {
  listTypes: () => benchmarkClient.get('/structure-types'),
  listNodes: (params = {}) =>
    benchmarkClient.get('/structure-nodes', { params }),
  createNode: (body) => benchmarkClient.post('/structure-nodes', body),
  updateNode: (id, body) => benchmarkClient.put(`/structure-nodes/${id}`, body),
  deleteNode: (id) => benchmarkClient.delete(`/structure-nodes/${id}`),
};

export const uploadsApi = {
  preview: (file) =>
    benchmarkClient.post(
      '/uploads/preview',
      toFormData({ file }),
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ),
  estimate: ({ file, projectId, contractId, primaryStructureType, columnMapping, datasetKind, bidderName }) =>
    benchmarkClient.post(
      '/uploads/estimate',
      toFormData({
        file,
        projectId,
        contractId,
        primaryStructureType,
        columnMapping,
        datasetKind: datasetKind || 'CONTRACT',
        bidderName,
      }),
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ),
  bid: ({ file, projectId, contractId, primaryStructureType, columnMapping, bidderName }) =>
    benchmarkClient.post(
      '/uploads/bid',
      toFormData({ file, projectId, contractId, primaryStructureType, columnMapping, bidderName }),
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ),
  changeOrder: ({ file, projectId, contractId, primaryStructureType, columnMapping }) =>
    benchmarkClient.post(
      '/uploads/change-order',
      toFormData({ file, projectId, contractId, primaryStructureType, columnMapping }),
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ),
  actual: ({ file, projectId, contractId, primaryStructureType, columnMapping }) =>
    benchmarkClient.post(
      '/uploads/actual',
      toFormData({ file, projectId, contractId, primaryStructureType, columnMapping }),
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ),
  getBatch: (batchId) => benchmarkClient.get(`/uploads/${batchId}`),
  listBatches: (projectId) =>
    benchmarkClient.get('/uploads', { params: projectId ? { projectId } : {} }),
};

export const bidComparisonApi = {
  forProject: (projectId) => benchmarkClient.get(`/projects/${projectId}/bid-comparison`),
};

/** Bid Import AI: Excel → MasterFormat JSON → bid uploads */
export const aiBoqApi = {
  transformBoq: ({ file, contractorName }) =>
    benchmarkClient.post(
      '/ai/transform-boq',
      toFormData({ file, contractorName }),
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      }
    ),
  importJson: ({ document, projectId, contractId, primaryStructureType }) =>
    benchmarkClient.post('/ai/import-json', document, {
      params: { projectId, contractId, primaryStructureType },
      timeout: 300000,
    }),
};

export const costsApi = {
  projectSummary: (projectId) => benchmarkClient.get(`/projects/${projectId}/costs`),
  structureCost: (projectId, structureType) =>
    benchmarkClient.get(`/projects/${projectId}/structure-cost`, {
      params: structureType ? { structureType } : {},
    }),
  transactions: (projectId, params = {}) =>
    benchmarkClient.get(`/projects/${projectId}/transactions`, { params }),
};

/** AsroTrack-style grid column customization (config_de_view + saved views). */
export const viewConfigApi = {
  getDefault: (category) =>
    benchmarkClient.get('/view-config/default', { params: { category } }),
  listViews: (category, params = {}) =>
    benchmarkClient.get('/view-config/views', { params: { category, ...params } }),
  saveView: (body) => benchmarkClient.post('/view-config/views', body),
  deleteView: (viewId) => benchmarkClient.delete(`/view-config/views/${viewId}`),
};

export const dashboardApi = {
  stats: () => benchmarkClient.get('/dashboard/stats'),
};

/** Tenant-scoped column mapping templates (DB). */
export const mappingTemplatesApi = {
  list: (uploadType = 'estimate', projectId) =>
    benchmarkClient.get('/mapping-templates', {
      params: {
        uploadType,
        ...(projectId ? { projectId } : {}),
      },
    }),
  save: (payload) => benchmarkClient.post('/mapping-templates', payload),
  delete: (id) => benchmarkClient.delete(`/mapping-templates/${id}`),
};

/** Phase-2 Project Comparison APIs */
export const comparisonsApi = {
  list: () => benchmarkClient.get('/comparisons'),
  getById: (id) => benchmarkClient.get(`/comparisons/${id}`),
  create: (payload) => benchmarkClient.post('/comparisons', payload),
  addProjects: (id, projectIds) =>
    benchmarkClient.post(`/comparisons/${id}/projects`, { projectIds }),
  removeProject: (id, projectId) =>
    benchmarkClient.delete(`/comparisons/${id}/projects/${projectId}`),
  calculate: (id) => benchmarkClient.post(`/comparisons/${id}/calculate`),
  overview: (id) => benchmarkClient.get(`/comparisons/${id}/overview`),
  structures: (id, structureType = 'CBS') =>
    benchmarkClient.get(`/comparisons/${id}/structures`, { params: { structureType } }),
  budgetCodeGrid: (id, { structureType = 'CBS', viewMetric = 'BUDGET' } = {}) =>
    benchmarkClient.get(`/comparisons/${id}/budget-code-grid`, {
      params: { structureType, viewMetric },
    }),
  budgetCodeLines: (id, structureCode) =>
    benchmarkClient.get(`/comparisons/${id}/budget-code-lines`, {
      params: { structureCode },
    }),
  unitRates: (id) => benchmarkClient.get(`/comparisons/${id}/unit-rates`),
  drillDown: (id, structureCode) =>
    benchmarkClient.get(`/comparisons/${id}/drill-down`, { params: { structureCode } }),
  filterPeers: (id, payload) =>
    benchmarkClient.post(`/comparisons/${id}/filter-peers`, payload),
};

/** Canonical upload fields shown in mapping dropdowns (labels for UI). */
export const MAPPING_FIELD_LABELS = {
  structureCode: 'Structure / Budget Code *',
  boqItemNumber: 'BoQ Item Number',
  description: 'Description *',
  quantity: 'Quantity',
  unit: 'Unit',
  unitPrice: 'Unit Price',
  amount: 'Amount',
  laborCost: 'Labor Cost',
  materialCost: 'Material Cost',
  equipmentCost: 'Equipment Cost',
  subcontractCost: 'Subcontract Cost',
  changeOrderNumber: 'Change Order Number',
  progressPercentage: 'Progress Percentage',
  transactionDate: 'Transaction Date',
};

export const defaultColumnMapping = Object.fromEntries(
  Object.keys(MAPPING_FIELD_LABELS).map((k) => [k, ''])
);

export default {
  projectsApi,
  contractsApi,
  structuresApi,
  uploadsApi,
  bidComparisonApi,
  costsApi,
  viewConfigApi,
  dashboardApi,
  mappingTemplatesApi,
  comparisonsApi,
};
